<template>
    <div>
        <div v-if="activityContent.content" class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content" />
                </e-field>
            </div>
        </div>

        <div v-if="flashcardSet && sessionLoaded">
            <flashcard-player
                :cards="playerCards"
                :title="flashcardSet.title"
                :read-only="readOnly"
                :show-card-preview="admin || instructor"
                :session-size="flashcardSet.session_size || null"
                :total-deck-cards="totalDeckCards"
                @complete="onSessionComplete"
                @study-again="onStudyAgain"
                @back="resetView"
            />
        </div>

        <div v-else-if="!flashcardSet && !loading" class="notification is-warning is-light">
            No flashcard set is associated with this activity.
        </div>

        <div v-if="loading" class="has-text-centered p-lg">
            <e-loading :is-full-page="false" active />
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import DisplayContent from '@/components/editor/DisplayContent.vue';
import FlashcardPlayer from '@/components/flashcard-player/FlashcardPlayer.vue';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import axios from '@/plugins/axios.js';
import flashcardService from '@/services/flashcardService.js';

export default {
    name: 'FlashcardActivityComponent',

    components: {
        DisplayContent,
        FlashcardPlayer,
    },

    mixins: [IsLateMixin, CourseActivityMixin],

    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        flashcardSet: null,
        sessionCards: [],
        totalDeckCards: 0,
        loading: true,
        sessionLoaded: false,
        sessionPayload: null,
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        playerCards() {
            const cards = this.sessionCards.length ? this.sessionCards : this.flashcardCards;
            const sessionSize = this.flashcardSet?.session_size;
            if (sessionSize && cards.length > sessionSize) {
                return cards.slice(0, sessionSize);
            }
            return cards;
        },

        flashcardCards() {
            if (!this.flashcardSet) return [];

            if (this.flashcardSet.sections && this.flashcardSet.sections.length) {
                return this.flashcardSet.sections.reduce((cards, section) => {
                    const sectionCards = (section.cards || []).map(card => ({
                        ...card,
                        imageUrl: section.image_url || null,
                    }));
                    return cards.concat(sectionCards);
                }, []);
            }

            return this.flashcardSet.cards || [];
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: this.sessionPayload || { cards_viewed: 0 },
            };
        },
    },

    async created() {
        try {
            const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
            const endpoint = this.namespace === 'Activities' ? 'activities' : 'activity-templates';

            const { data } = await axios.get(
                `/api/${endpoint}/${id}?include=contentable.flashcardSet.sections.cards,contentable.flashcardSet.sections.media`
            );

            const flashcardSetData = data.data.activity_content?.flashcard_set;

            if (flashcardSetData) {
                this.flashcardSet = flashcardSetData;
                if (this.namespace === 'Activities') {
                    await this.loadSession();
                } else {
                    this.totalDeckCards = this.flashcardCards.length;
                }
                this.sessionLoaded = true;
            }
        } catch (error) {
            this.$handleResponseError(error);
        } finally {
            this.loading = false;
        }
    },

    methods: {
        async onSessionComplete(submissionData) {
            this.sessionPayload = submissionData;

            if (this.namespace === 'Activities' && submissionData.card_responses?.length) {
                try {
                    const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                    await flashcardService.submitResponses(id, submissionData.card_responses);
                } catch (error) {
                    console.warn('Failed to save flashcard session responses:', error);
                }
            }

            if (!this.completed && !this.readOnly) {
                await this.submit();
            }
        },

        async loadSession() {
            try {
                const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                const data = await flashcardService.getSession(id);
                this.sessionCards = (data.cards || []).map(card => ({
                    ...card,
                    imageUrl: card.image_url || null,
                }));
                this.totalDeckCards = data.total_cards || this.flashcardCards.length;
            } catch (error) {
                console.warn('Flashcard session load failed, falling back to full deck:', error);
                this.sessionCards = [];
                this.totalDeckCards = this.flashcardCards.length;
            }
        },

        async onStudyAgain() {
            if (this.namespace === 'Activities') {
                await this.loadSession();
            }
        },

        async resetView() {
            if (this.namespace === 'Activities') {
                await this.loadSession();
            }
        },
    },
};
</script>
